import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";

const TAB_IDS = ["producao", "staff"];

const iniciais = (nome) => nome.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const SecaoTitulo = ({ children, testid }) => (
    <div className="flex items-center gap-4 mb-8" data-testid={testid}>
        <h2 className="font-caps text-xs sm:text-sm tracking-[0.3em] uppercase gold-text shrink-0">{children}</h2>
        <div className="flex-1 h-px bg-[#c9a24b]/20" aria-hidden="true" />
    </div>
);

// Lista (sem fotografias): iniciais, papel, nome e descrição.
const MembroItem = ({ m }) => (
    <li className="flex items-start gap-5 sm:gap-6 py-6 first:pt-0 last:pb-0" data-testid={`equipa-membro-${m.id}`}>
        <span className="w-12 h-12 rounded-full border border-[#c9a24b]/50 flex items-center justify-center font-display font-medium gold-text text-sm shrink-0 mt-1" aria-hidden="true">
            {iniciais(m.nome)}
        </span>
        <div className="min-w-0">
            <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f]">{m.papel}</p>
            <h3 className="font-display font-medium gold-text text-2xl sm:text-3xl mt-1 leading-tight">{m.nome}</h3>
            <p className="font-light text-[#efe6d6]/70 text-sm sm:text-base leading-relaxed mt-2.5 max-w-2xl">{m.texto}</p>
        </div>
    </li>
);

const Lista = ({ children, testid }) => (
    <ul className="divide-y divide-[#c9a24b]/15 border border-[#c9a24b]/15 bg-[#0a0705]/60 px-6 sm:px-8 py-6" data-testid={testid}>{children}</ul>
);

const MembroLinha = ({ nome, papel, testid }) => (
    <div className="flex items-center gap-4" data-testid={testid}>
        <span className="w-11 h-11 rounded-full border border-[#c9a24b]/50 bg-[#070503] flex items-center justify-center font-display font-medium gold-text text-sm shrink-0" aria-hidden="true">
            {iniciais(nome)}
        </span>
        <div>
            <p className="font-display font-light text-lg text-[#efe6d6]/90 leading-tight">{nome}</p>
            {papel && <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#d19a3f]/80 mt-1">{papel}</p>}
        </div>
    </div>
);

export default function EquipaPage() {
    const [searchParams] = useSearchParams();
    const tabInicial = TAB_IDS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "producao";
    const [tab, setTab] = useState(tabInicial);
    const { t } = useLanguage();
    useSEO({
        title: t.seo.equipa.title,
        description: t.seo.equipa.desc,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.equipa, path: "/equipa" }]),
    });

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative" data-testid="equipa-page">
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1100px] px-5 sm:px-10 pt-44 sm:pt-32 pb-24"
            >
                <Reveal className="text-center">
                    <h1 className="font-display font-medium gold-text text-4xl sm:text-5xl tracking-[0.08em]" data-testid="equipa-title">
                        {t.equipa.titulo}
                    </h1>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                </Reveal>

                <div className="flex flex-wrap justify-center gap-2 sm:gap-6 border-b border-[#c9a24b]/20 mt-12" data-testid="equipa-tabs">
                    {TAB_IDS.map((id) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            data-testid={`tab-${id}`}
                            className={`font-caps text-xs sm:text-sm tracking-[0.2em] uppercase px-4 py-3 -mb-px border-b-2 transition-colors duration-300 ${
                                tab === id ? "border-[#c9a24b] text-[#c9a24b]" : "border-transparent text-[#efe6d6]/55 hover:text-[#efe6d6]"
                            }`}
                        >
                            {t.equipa.tabs[id]}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={tab}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4 }}
                        className="mt-12"
                    >
                        {tab === "producao" && (
                            <div className="max-w-3xl mx-auto" data-testid="producao-content">
                                <Reveal className="text-center mb-12">
                                    <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#d19a3f]" data-testid="producao-titulo">{t.equipa.producaoTitulo}</p>
                                </Reveal>
                                <Reveal>
                                    <Lista testid="producao-lista">
                                        {t.equipa.producao.map((m) => <MembroItem key={m.id} m={m} />)}
                                    </Lista>
                                </Reveal>
                            </div>
                        )}
                        {tab === "staff" && (
                            <div className="max-w-3xl mx-auto space-y-16" data-testid="staff-content">
                                <Reveal className="text-center">
                                    <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#d19a3f]" data-testid="staff-titulo">{t.equipa.staffTitulo}</p>
                                </Reveal>
                                <section>
                                    <SecaoTitulo testid="staff-cozinha-titulo">{t.equipa.cozinhaTitulo}</SecaoTitulo>
                                    <Lista testid="cozinha-lista">
                                        {t.equipa.cozinha.map((m) => <MembroItem key={m.id} m={m} />)}
                                    </Lista>
                                    <div className="mt-10 border border-[#c9a24b]/20 bg-[#0a0705]/70 px-6 py-6 sm:px-8" data-testid="staff-apoio-card">
                                        <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] text-center">{t.equipa.apoioTitulo}</p>
                                        <div className="mt-6 flex flex-wrap justify-center gap-x-10 gap-y-5">
                                            {t.equipa.apoio.map((nome) => (
                                                <MembroLinha key={nome} nome={nome} testid={`staff-apoio-${nome.toLowerCase().replace(/\s+/g, "-")}`} />
                                            ))}
                                        </div>
                                    </div>
                                </section>
                                <section>
                                    <SecaoTitulo testid="staff-sala-titulo">{t.equipa.salaTitulo}</SecaoTitulo>
                                    <Lista testid="sala-lista">
                                        <MembroItem m={t.equipa.sala} />
                                    </Lista>
                                    <div className="mt-8 flex justify-center sm:justify-start sm:pl-8">
                                        <MembroLinha nome={t.equipa.salaApoio.nome} papel={t.equipa.salaApoio.papel} testid="staff-sala-apoio" />
                                    </div>
                                </section>
                                <section>
                                    <SecaoTitulo testid="staff-bar-titulo">{t.equipa.barTitulo}</SecaoTitulo>
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6 border border-[#c9a24b]/20 bg-[#0a0705]/70 px-6 py-6 sm:px-8" data-testid="staff-bar-card">
                                        <MembroLinha nome={t.equipa.barMembro.nome} papel={t.equipa.barMembro.papel} testid="staff-bar-membro" />
                                        <Link
                                            to="/experiencia?tab=bar"
                                            data-testid="staff-bar-horario-link"
                                            className="shrink-0 font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-6 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400"
                                        >
                                            {t.experiencia.lineupLink}
                                        </Link>
                                    </div>
                                </section>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </motion.main>
        </div>
    );
}
